<template>
  <div>
    <v-card-title class="pa-1">
      <h3 :class="fieldError ? 'red--text wiggle' : 'white--text'" v-html="field.label"></h3>
    </v-card-title>
    <v-card-subtitle v-if="field.helpText" class="pa-1">
      <p class="mt-1 grey--text text--lighten-1 text-caption font-weight-regular">{{ field.helpText }}</p>
    </v-card-subtitle>

    <div v-if="field.multivalued">
      <div v-if="cloudinaryFormat">
        <draggable v-if="!forceRenderer" v-model="newValue" class="row">
          <v-col v-for="(item, index) in newValue" v-if="cloudinaryFormat" :key="index" cols="3">
            <v-card color="primary">
              <v-img :src="fullUrl(item.url)" contain style="min-height: 60px" />

              <v-card-actions v-if="$store.getters.permissionToWrite">
                <v-spacer />

                <v-btn icon @click="editMultiValuedEntity(index)">
                  <v-icon>edit</v-icon>
                </v-btn>

                <v-btn v-if="store.getters.permissionToWrite" icon @click="deleteValue()">
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
            <v-card class="pa-2 mt-2" color="primary">
              <strong class="align-content-center">
                {{ buildLabel(item) }}
              </strong>
            </v-card>
          </v-col>
        </draggable>
      </div>

      <div v-else>
        <draggable v-if="!forceRenderer && !cloudinaryFormat" v-model="newValue">
          <div v-for="(item, index) in newValue" :key="index" class="pa-3">
            <v-chip>
              <a v-if="showEditOption" class="graylink cursor-as-pointer" target="_blank" @click="editMultiValuedEntity(index)">
                <v-avatar>
                  <v-icon small>edit</v-icon>
                </v-avatar>
              </a>

              <strong class="mr-2" :class="{ 'move-cursor': newValue.length > 1 }">
                {{ buildLabel(item) }}
              </strong>

              <a v-if="store.getters.permissionToWrite && showEditOption" @click="deleteMultiValue(index)">
                <v-icon small>delete</v-icon>
              </a>
            </v-chip>
          </div>
        </draggable>
      </div>

      <v-btn v-if="store.getters.permissionToWrite && showEditOption" slot="footer" icon :disabled="isAddNewDisabled" @click="newMultiValuedEntity()">
        <v-icon :color="fieldError ? 'red' : ''">add_to_photos</v-icon>
      </v-btn>
    </div>

    <div v-else>
      <v-col v-if="cloudinaryFormat && newValue" cols="5">
        <v-card color="primary">
          <v-img :src="fullUrl(newValue.url)" contain style="min-height: 60px" />

          <v-card-actions v-if="$store.getters.permissionToWrite">
            <v-spacer />

            <v-btn icon @click="editSingleValueEntity()">
              <v-icon>edit</v-icon>
            </v-btn>

            <v-btn v-if="store.getters.permissionToWrite" icon @click="deleteValue()">
              <v-icon>delete</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card class="pa-2 mt-2" color="primary">
          <strong class="align-content-center">
            {{ buildLabel(newValue) }}
          </strong>
        </v-card>
      </v-col>

      <div v-else>
        <v-chip v-if="newValue">
          <a class="graylink cursor-as-pointer" target="_blank" @click="editSingleValueEntity()">
            <v-avatar>
              <v-icon small>edit</v-icon>
            </v-avatar>
          </a>

          <strong class="mr-2">
            {{ buildLabel(newValue) }}
          </strong>

          <a v-if="store.getters.permissionToWrite" @click="deleteValue()">
            <v-icon small>delete</v-icon>
          </a>
        </v-chip>

        <v-btn v-if="store.getters.permissionToWrite && !newValue" slot="footer" icon @click="newSingleValuedEntity()">
          <v-icon :color="fieldError ? 'red' : ''">add_to_photos</v-icon>
        </v-btn>
      </div>
    </div>
    <v-card-subtitle v-if="fieldError" class="px-0 py-0">
      <span class="red--text">{{ fieldError }}</span>
    </v-card-subtitle>

    <v-text-field v-show="false" ref="entityRules" v-model="newValue" :rules="field.rules" readonly />

    <EntityPopup
      ref="entityPopup"
      :raw-content-types="rawContentTypes"
      :content-type="entityContentType"
      :update="update"
      :create="create"
    />
  </div>
</template>
<script setup>

import { ref, computed, watch, nextTick, onMounted, toRefs, getCurrentInstance } from 'vue';

import draggable from 'vuedraggable';
import { useStore } from 'vue2-helpers/vuex';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';

import { getContentTypeByConfigArg, getMediaUrl } from '@/utils/content';

import EntityPopup from './Entity/EntityPopup.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

// eslint-disable-next-line no-undef
const props = defineProps({
  field: Object,
  rawContentTypes: Object,
  value: [String, Array, Object]
});
const entityRules = ref(null);
const changed = ref(false);

const fieldError = computed(() => {
  if (!changed.value) {
    return false;
  }

  if (entityRules.value?.valid) {
    return false;
  }

  return (entityRules.value && entityRules.value.errorBucket.length) ? entityRules.value.errorBucket[0] : null;
});

const showEditOption = computed(() => props.field.readonly !== true);

const newValue = ref(null);

if (props.value) {
  newValue.value = JSON.parse(JSON.stringify(props.value));
} else if (props.field.multivalued) {
  newValue.value = [];
}

const forceRenderer = ref(false);

const entityContentType = getContentTypeByConfigArg('type', props.field.type, props.rawContentTypes);
const cloudinaryFormat = ref(entityContentType.configs?.format === 'Cloudinary');

const isAddNewDisabled = computed(() => {
  if (props.field.multivalued && props.field.maxCount) {
    return newValue.value.length >= props.field.maxCount;
  }
  return false;
});

const fullUrl = path => getMediaUrl(path, 'w_150,h_100');

const buildLabel = item => {
  const labelValues = [];
  Object.keys(entityContentType.fields).forEach(key => {
    if (cloudinaryFormat.value && key === 'url') {
      return;
    }

    const entityFieldConfig = entityContentType.fields[key];

    if (entityFieldConfig.showInList && item && item[key]) {
      if (['Picker', 'Entity'].includes(entityFieldConfig.viewType)) {
        let fieldContentType = null;
        if (entityFieldConfig.viewType === 'Picker') {
          fieldContentType = getContentTypeByConfigArg('wtype', entityFieldConfig.contentWtype, props.rawContentTypes);

          if (item[key].wtitle) {
            labelValues.push(item[key].wtitle);
          }
        }

        if (entityFieldConfig.viewType === 'Entity') {
          fieldContentType = getContentTypeByConfigArg('type', entityFieldConfig.type, props.rawContentTypes);
        }

        Object.keys(fieldContentType.fields).forEach(fieldKey => {
          if (fieldContentType.fields[fieldKey].showInList && item[key][fieldKey]) {
            labelValues.push(item[key][fieldKey]);
          }
        });
      } else if (entityFieldConfig.optionsByReference) {
        let values = [];

        if (item[key]) {
          values = entityFieldConfig.multivalued ? item[key] : [item[key]];
        }

        const reference = store.getters.getReference(entityFieldConfig.optionsByReference);
        labelValues.push(values.map(v => (reference[v] || v)).join(', '));
      } else {
        labelValues.push(item[key]);
      }
    }
  });

  let label = labelValues.length ? labelValues.join(' | ') : 'NO DATA';

  if (label.length > 100 && cloudinaryFormat.value) {
    label = label.slice(0, 100);
    label = `${label} ...`;
  }

  return label;
};

const emit = defineEmits(['input']);

const emitNewValue = () => {
  changed.value = true;

  // FORCES RENDER LIST
  forceRenderer.value = true;
  nextTick(() => {
    forceRenderer.value = false;
  });

  // console.log('################### emitNewValue');
  // emit('input', newValue.value);
  emit('input', JSON.parse(JSON.stringify(newValue.value)));
};

const entityPopup = ref(null);

let editItem = null;

const editMultiValuedEntity = index => {
  editItem = toRefs(newValue.value)[index];
  entityPopup.value.editItem(editItem.value);
};

const newMultiValuedEntity = () => {
  editItem = null;
  entityPopup.value.newItem();
};

const deleteMultiValue = index => {
  newValue.value.splice(index, 1);
  emitNewValue();
};

const editSingleValueEntity = () => {
  editItem = toRefs(newValue.value);

  entityPopup.value.editItem(editItem);
};

const newSingleValuedEntity = () => {
  entityPopup.value.newItem();
};

const deleteValue = () => {
  newValue.value = null;
  emitNewValue();
};

const update = value => {
  // If is editing an item
  if (editItem) {
    editItem.value = value;
  }
  emitNewValue();
};

const create = value => {
  if (props.field.multivalued) {
    newValue.value = newValue.value || [];
    newValue.value.push(value);
  } else {
    newValue.value = value;
  }

  emitNewValue();
};

// When the order change with draggable
watch(newValue, val => {
  emit('input', val);
}, { deep: true });

onMounted(() => {
  if (route.query.variation && newValue.value && entityContentType.configs.hasOwnProperty('wtype')) {
    if (props.field.multivalued) {
      const index = newValue.value.findIndex(v => v.uuid && v.uuid === route.query.variation);
      if (index !== -1) {
        editMultiValuedEntity(index);
      } else {
        console.log('[onMounted] Variation not found');
      }
    } else {
      editSingleValueEntity();
    }
  }
});

</script>
